import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    FaBookOpen, FaChevronDown, FaChevronLeft, FaChevronRight,
    FaCopy, FaDownload, FaEdit, FaFileAlt, FaFolderOpen, FaPlus,
    FaArchive, FaRegStar, FaSave, FaSearch, FaStar, FaArrowUp, FaArrowDown,
    FaHistory, FaEllipsisH, FaLevelUpAlt, FaTimes, FaEye, FaInfoCircle, FaSlidersH,
} from "react-icons/fa";
import { MANUALS_CONSTANTS } from "../_MODULES/_API";
import { ManualFormModal } from "../_UI/ManualFormModal";
import { ManualMarkdownEditor, getMarkdownOutline } from "../_UI/ManualMarkdownEditor";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { ReadOnlyInfo } from "../../../../../_CORE/APP/Templates/_UI/ReadOnly/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { downloadMarkdownDocument, downloadMarkdownTreeZip, downloadPdfDocument, downloadPdfTree } from "../../../../_UI/DocumentExport";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { REACT_HOT_TOAST_ERROR } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { isDuplicateRecord, normalizeUniqueValue } from "../../../../_UI/recordUniqueness";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import "../_UI/_Styles/Manuals.css";

const apiPayload = (response) => response?.data ?? response;
const apiSuccess = (response) => {
    const status = Number(response?.status ?? response?.code_status ?? 200);
    return status >= 200 && status < 300 && response?.success !== false;
};

const replacePath = (endpointKey, values = {}) => {
    let url = API_CORE.ENDPOINTS[endpointKey];
    Object.entries(values).forEach(([key, value]) => {
        url = url.replace(`:${key}`, String(value));
    });
    return url;
};



const sortPages = (pages = []) => [...pages].sort((a, b) =>
    Number(a.position || 0) - Number(b.position || 0)
    || String(a.title || "").localeCompare(String(b.title || ""), "es"),
);

const buildTree = (pages = []) => {
    const map = new Map();
    sortPages(pages).forEach((page) => map.set(page.id, { ...page, children: [] }));
    const roots = [];

    map.forEach((page) => {
        const parent = page.parentId ? map.get(page.parentId) : null;
        if (parent && parent.id !== page.id) parent.children.push(page);
        else roots.push(page);
    });

    const deepSort = (items) => {
        items.sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
        items.forEach((item) => deepSort(item.children));
        return items;
    };

    return deepSort(roots);
};

const collectDescendants = (pages, pageId) => {
    const childrenByParent = new Map();
    pages.forEach((page) => {
        const key = page.parentId || "__ROOT__";
        if (!childrenByParent.has(key)) childrenByParent.set(key, []);
        childrenByParent.get(key).push(page.id);
    });

    const found = new Set();
    const walk = (id) => {
        (childrenByParent.get(id) || []).forEach((childId) => {
            if (found.has(childId)) return;
            found.add(childId);
            walk(childId);
        });
    };
    walk(pageId);
    return found;
};

const getPagePath = (pages, pageId) => {
    const byId = new Map(pages.map((page) => [page.id, page]));
    const path = [];
    const visited = new Set();
    let current = byId.get(pageId);

    while (current && !visited.has(current.id)) {
        visited.add(current.id);
        path.unshift(current);
        current = current.parentId ? byId.get(current.parentId) : null;
    }
    return path;
};

export const ManualDetailViewMain = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const titleRef = useRef(null);
    const useWorkspaceMode = new URLSearchParams(location.search).get("mode") === "edit" ? "edit" : "read";
    const IS_READ_ONLY = useWorkspaceMode === "read";

    const [useManualOpen, setManualOpen] = useState(false);
    const [useManualMeta, setManualMeta] = useState(null);
    const [usePages, setPages] = useState([]);
    const [useSelectedId, setSelectedId] = useState(null);
    const [useDraft, setDraft] = useState({ title: "", content: "", icon: "" });
    const [useSaveState, setSaveState] = useState("saved");
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [useAdvanced, setAdvanced] = useState({ location: "ALL", favorite: "ALL", content: "ALL" });
    const [useCollapsed, setCollapsed] = useState(() => new Set());
    const [useScrollHeadingIndex, setScrollHeadingIndex] = useState(null);
    const [useEditorMode, setEditorMode] = useState(() => {
        try { return localStorage.getItem("manuals:editor-mode") || "split"; }
        catch { return "split"; }
    });
    const [useImporting, setImporting] = useState(false);
    const [useFullscreenOpen, setFullscreenOpen] = useState(true);
    const [usePageMenuOpen, setPageMenuOpen] = useState(null);

    const { apiGet: onGetManual, useApiData: MANUAL, useApiLoading: IS_LOADING } = useApiQuery({
        minTimeQuery: 200, enablePagination: false, startLoading: true,
    });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 180,
    });

    const onLoad = useCallback(() => onGetManual({
        endpoint: MANUALS_CONSTANTS.DETAIL,
        isDetail: true,
        config: { pathParams: { id } },
    }), [id, onGetManual]);

    useEffect(() => { onLoad(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!MANUAL) return;
        setManualMeta(MANUAL);
        setPages(sortPages(MANUAL.pages || []));
    }, [MANUAL]);

    const CURRENT_MANUAL = useManualMeta || MANUAL;

    useEffect(() => {
        setFullscreenOpen(true);
    }, [id, useWorkspaceMode]);

    useEffect(() => {
        setSearch("");
        setAdvancedOpen(false);
        setAdvanced({ location: "ALL", favorite: "ALL", content: "ALL" });
    }, [id]);

    useEffect(() => {
        try {
            const raw = localStorage.getItem(`manuals:collapsed:${id}`);
            setCollapsed(new Set(raw ? JSON.parse(raw) : []));
        } catch {
            setCollapsed(new Set());
        }
    }, [id]);

    useEffect(() => {
        try { localStorage.setItem(`manuals:collapsed:${id}`, JSON.stringify([...useCollapsed])); }
        catch { /* localStorage puede estar bloqueado */ }
    }, [id, useCollapsed]);

    useEffect(() => {
        try { localStorage.setItem("manuals:editor-mode", useEditorMode); }
        catch { /* localStorage puede estar bloqueado */ }
    }, [useEditorMode]);

    useEffect(() => {
        if (!useFullscreenOpen) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = previousOverflow; };
    }, [useFullscreenOpen]);

    useEffect(() => {
        if (!usePageMenuOpen) return undefined;
        const onPointerDown = (event) => {
            if (!event.target?.closest?.(".LStudio__manual_page_menu_wrap")) setPageMenuOpen(null);
        };
        const onEscape = (event) => {
            if (event.key === "Escape") setPageMenuOpen(null);
        };
        document.addEventListener("mousedown", onPointerDown);
        window.addEventListener("keydown", onEscape);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            window.removeEventListener("keydown", onEscape);
        };
    }, [usePageMenuOpen]);

    const selectedPage = useMemo(
        () => usePages.find((page) => page.id === useSelectedId) || null,
        [usePages, useSelectedId],
    );

    const hasDraftChanges = useMemo(() => {
        if (!selectedPage) return false;
        return (useDraft.title || "") !== (selectedPage.title || "")
            || (useDraft.content || "") !== (selectedPage.content || "")
            || (useDraft.icon || "") !== (selectedPage.icon || "");
    }, [selectedPage, useDraft]);

    useEffect(() => {
        if (!usePages.length) {
            setSelectedId(null);
            return;
        }

        const queryId = new URLSearchParams(location.search).get("page");
        const target = usePages.find((page) => page.id === queryId)
            || usePages.find((page) => !page.parentId)
            || usePages[0];

        if (!useSelectedId || !usePages.some((page) => page.id === useSelectedId) || (queryId && queryId !== useSelectedId)) {
            setSelectedId(target.id);
        }
    }, [usePages, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!useSelectedId) {
            setDraft({ title: "", content: "", icon: "" });
            return;
        }
        const page = usePages.find((item) => item.id === useSelectedId);
        if (!page) return;
        setDraft({ title: page.title || "", content: page.content || "", icon: page.icon || "" });
        setSaveState("saved");
        setScrollHeadingIndex(null);
    }, [useSelectedId]); // intentionally only page identity

    const onPatchPage = useCallback(async (pageId, data) => {
        const endpoint = API_CORE.ENDPOINTS[MANUALS_CONSTANTS.PAGE_UPDATE];
        const response = await API_CORE.SERVICES.PATCH_resource(endpoint, pageId, data);
        return { success: apiSuccess(response), data: apiPayload(response), raw: response };
    }, []);

    const onSaveDraft = useCallback(async () => {
        if (IS_READ_ONLY) return true;
        if (!selectedPage || !hasDraftChanges || !useDraft.title.trim()) return true;

        const pageId = selectedPage.id;
        if (isDuplicateRecord({ rows: usePages, value: useDraft.title, currentId: pageId, getValue: (row) => row?.title, scope: selectedPage.parentId || "", getScope: (row) => row?.parentId || "" })) {
            REACT_HOT_TOAST_ERROR("Ya existe una página con ese título en este nivel");
            setSaveState("error");
            return false;
        }
        const snapshot = {
            title: useDraft.title.trim(),
            content: useDraft.content || "",
            icon: useDraft.icon?.trim() || undefined,
        };

        setSaveState("saving");
        const response = await onPatchPage(pageId, snapshot);

        if (!response.success) {
            setSaveState("error");
            return false;
        }

        const saved = response.data?.id ? response.data : { ...selectedPage, ...snapshot };
        setPages((current) => current.map((page) => page.id === pageId ? { ...page, ...saved, ...snapshot } : page));
        setSaveState("saved");
        return true;
    }, [IS_READ_ONLY, selectedPage, hasDraftChanges, useDraft, usePages, onPatchPage]);

    const onOpenFullscreen = useCallback(() => {
        setPageMenuOpen(null);
        setFullscreenOpen(true);
    }, []);

    const onCloseFullscreen = useCallback(async () => {
        setPageMenuOpen(null);
        if (!IS_READ_ONLY && hasDraftChanges) {
            const saved = await onSaveDraft();
            if (!saved) return;
        }
        navigate("/manuals");
    }, [IS_READ_ONLY, hasDraftChanges, onSaveDraft, navigate]);

    useEffect(() => {
        if (IS_READ_ONLY || !hasDraftChanges || !selectedPage || !useDraft.title.trim()) return undefined;
        setSaveState("dirty");
        const timer = window.setTimeout(() => { onSaveDraft(); }, 850);
        return () => window.clearTimeout(timer);
    }, [hasDraftChanges, useDraft.title, useDraft.content, useDraft.icon, selectedPage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                document.getElementById("manual-page-search-fullscreen")?.focus();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const onSelectPage = async (pageId, { openFullscreen = false } = {}) => {
        if (pageId === useSelectedId) {
            if (openFullscreen) onOpenFullscreen();
            return;
        }
        if (hasDraftChanges) {
            const saved = await onSaveDraft();
            if (!saved) return;
        }
        setSelectedId(pageId);
        navigate(`/manuals/${id}?mode=${useWorkspaceMode}&page=${pageId}`, { replace: true });
        if (openFullscreen) window.setTimeout(() => setFullscreenOpen(true), 0);
    };

    const onPostPath = async (endpointKey, replacements, data = null) => {
        const url = replacePath(endpointKey, replacements);
        const response = await API_CORE.SERVICES.POST_RESOURCE(url, data);
        return { success: apiSuccess(response), data: apiPayload(response), raw: response };
    };

    const onCreatePage = async (parentId = null) => {
        if (IS_READ_ONLY) return;
        if (hasDraftChanges) await onSaveDraft();
        const siblings = usePages.filter((page) => String(page.parentId || "") === String(parentId || ""));
        let nextTitle = "Nueva pagina";
        let suffix = 2;
        while (siblings.some((page) => normalizeUniqueValue(page.title) === normalizeUniqueValue(nextTitle))) {
            nextTitle = `Nueva pagina ${suffix}`;
            suffix += 1;
        }
        const response = await onPostPath(MANUALS_CONSTANTS.PAGE_CREATE, { manualId: id }, {
            title: nextTitle,
            parentId: parentId || null,
            content: "",
        });
        if (!response.success || !response.data?.id) return;

        const page = response.data;
        setPages((current) => sortPages([...current, page]));
        setSelectedId(page.id);
        navigate(`/manuals/${id}?mode=edit&page=${page.id}`, { replace: true });
        setFullscreenOpen(true);
        window.setTimeout(() => {
            titleRef.current?.focus();
            titleRef.current?.select();
        }, 80);
    };

    const onArchivePage = async () => {
        if (!selectedPage) return;
        const descendantIds = collectDescendants(usePages, selectedPage.id);
        const branchIds = new Set([selectedPage.id, ...descendantIds]);
        const nestedCount = descendantIds.size;
        const message = nestedCount
            ? `Se archiva "${selectedPage.title}" junto con ${nestedCount} subpágina${nestedCount === 1 ? "" : "s"}. Podés restaurarlas desde Archivados.`
            : `Se archiva "${selectedPage.title}". Podés restaurarla desde Archivados.`;
        const ok = await confirm.danger("Archivar página", message);
        if (!ok) return;

        const snapshotPages = usePages;
        const snapshotSelectedId = selectedPage.id;
        const parentId = selectedPage.parentId || null;
        const remaining = usePages.filter((page) => !branchIds.has(page.id));
        const next = remaining.find((page) => page.id === parentId)
            || remaining.find((page) => !page.parentId)
            || remaining[0];

        // Local-first: desaparece la rama completa; el backend conserva todo en Archivados.
        setPageMenuOpen(null);
        setPages(remaining);
        if (next) {
            setSelectedId(next.id);
            navigate(`/manuals/${id}?mode=edit&page=${next.id}`, { replace: true });
        } else {
            setSelectedId(null);
            navigate(`/manuals/${id}?mode=edit`, { replace: true });
        }

        const response = await API_CORE.SERVICES.PATCH_resource(
            replacePath(MANUALS_CONSTANTS.PAGE_ARCHIVE, { id: snapshotSelectedId }),
            {},
        );

        if (!apiSuccess(response)) {
            setPages(snapshotPages);
            setSelectedId(snapshotSelectedId);
            navigate(`/manuals/${id}?mode=edit&page=${snapshotSelectedId}`, { replace: true });
            return;
        }
    };

    const onDuplicatePage = async () => {
        if (!selectedPage) return;
        if (hasDraftChanges) await onSaveDraft();
        const response = await onPostPath(MANUALS_CONSTANTS.PAGE_DUPLICATE, { id: selectedPage.id });
        if (!response.success || !response.data?.id) return;
        const page = response.data;
        setPages((current) => sortPages([...current, page]));
        await onSelectPage(page.id);
    };

    const onToggleFavorite = async () => {
        if (!selectedPage) return;
        const pageId = selectedPage.id;
        const previous = !!selectedPage.isFavorite;
        const next = !previous;

        // Local-first: reflejamos el favorito inmediatamente.
        setPages((current) => current.map((page) =>
            page.id === pageId ? { ...page, isFavorite: next } : page
        ));

        const response = await onPatchPage(pageId, { isFavorite: next });

        // Si el PATCH falla, revertimos solamente este campo; nunca recargamos el manual.
        if (!response.success) {
            setPages((current) => current.map((page) =>
                page.id === pageId ? { ...page, isFavorite: previous } : page
            ));
        }
    };

    const onChangeParent = async (nextParentId) => {
        if (!selectedPage) return;
        if (hasDraftChanges) await onSaveDraft();
        const pageId = selectedPage.id;
        const parentId = nextParentId || null;
        const nextPosition = usePages.filter((page) => (page.parentId || null) === parentId && page.id !== pageId).length;
        const snapshotPages = usePages;

        // Local-first: el arbol se reorganiza en este mismo render.
        setPages((current) => current.map((page) => page.id === pageId
            ? { ...page, parentId, position: nextPosition }
            : page));

        const response = await onPatchPage(pageId, { parentId, position: nextPosition });
        if (!response.success) setPages(snapshotPages);
    };

    const onMovePage = async (direction) => {
        if (!selectedPage) return;
        const siblings = sortPages(usePages.filter((page) => (page.parentId || null) === (selectedPage.parentId || null)));
        const index = siblings.findIndex((page) => page.id === selectedPage.id);
        const neighbor = siblings[index + direction];
        if (!neighbor) return;

        const pageId = selectedPage.id;
        const currentPosition = Number(selectedPage.position || index);
        const neighborPosition = Number(neighbor.position || (index + direction));
        const snapshotPages = usePages;

        // Local-first: el orden cambia en el acto.
        setPages((current) => sortPages(current.map((page) => {
            if (page.id === pageId) return { ...page, position: neighborPosition };
            if (page.id === neighbor.id) return { ...page, position: currentPosition };
            return page;
        })));

        const first = await onPatchPage(pageId, { position: neighborPosition });
        if (!first.success) {
            setPages(snapshotPages);
            return;
        }

        const second = await onPatchPage(neighbor.id, { position: currentPosition });
        if (!second.success) {
            setPages(snapshotPages);
            // Compensacion best-effort para que backend y UI no queden desalineados.
            await onPatchPage(pageId, { position: currentPosition });
        }
    };

    const onChangeIcon = async () => {
        if (!selectedPage) return;
        const next = window.prompt("Icono o emoji de la pagina", useDraft.icon || "📄");
        if (next === null) return;
        setDraft((current) => ({ ...current, icon: next.trim() }));
    };

    const onSaveManual = async (values) => {
        const previous = CURRENT_MANUAL ? { ...CURRENT_MANUAL } : null;

        // Local-first: la identidad/configuracion del manual cambia en pantalla
        // antes de esperar al backend. Si falla, volvemos al snapshot anterior.
        setManualMeta((current) => ({ ...(current || CURRENT_MANUAL || {}), ...values }));

        const response = await actionMutate({
            method: "PATCH", endpoint: MANUALS_CONSTANTS.UPDATE, id,
            data: values, successMessage: "Manual actualizado",
        });
        if (!response.success) {
            if (previous) setManualMeta(previous);
            return false;
        }

        const responseData = response?.data?.data || response?.data || null;
        if (responseData?.id) {
            setManualMeta((current) => ({ ...(current || {}), ...responseData }));
        }
        return true;
    };


    const onImportLegacy = async () => {
        const remaining = Math.max(0, Number(CURRENT_MANUAL?.legacyEntryCount || 0)
            - usePages.filter((page) => page.legacyEntryId).length);
        const ok = await confirm.warning(
            "Convertir contenido anterior",
            `Se convertiran ${remaining} entrada(s) anteriores a paginas Markdown. Los datos originales se conservaran como respaldo.`,
        );
        if (!ok) return;
        setImporting(true);
        const response = await onPostPath(MANUALS_CONSTANTS.PAGE_IMPORT_LEGACY, { manualId: id });
        setImporting(false);
        if (response.success) await onLoad();
    };

    const onCurrentPagesForExport = () => usePages.map((page) => page.id === selectedPage?.id
        ? { ...page, title: useDraft.title.trim() || page.title, content: useDraft.content || "", icon: useDraft.icon || page.icon }
        : page);

    const onExportPageMarkdown = () => {
        if (!selectedPage) return;
        downloadMarkdownDocument({
            title: useDraft.title.trim() || selectedPage.title,
            content: useDraft.content || "",
        });
    };

    const onExportPagePdf = () => {
        if (!selectedPage) return;
        downloadPdfDocument({
            title: useDraft.title.trim() || selectedPage.title,
            content: useDraft.content || "",
        });
    };

    const tree = useMemo(() => buildTree(usePages), [usePages]);

    const onExportAllPdf = async () => {
        if (hasDraftChanges && !(await onSaveDraft())) return;
        downloadPdfTree({
            title: CURRENT_MANUAL?.name || "Manual",
            description: CURRENT_MANUAL?.description || "",
            tree: buildTree(onCurrentPagesForExport()),
        });
    };

    const onExportAllMarkdown = async () => {
        if (hasDraftChanges && !(await onSaveDraft())) return;
        downloadMarkdownTreeZip({
            title: CURRENT_MANUAL?.name || "Manual",
            description: CURRENT_MANUAL?.description || "",
            tree: buildTree(onCurrentPagesForExport()),
        });
    };

    const query = useSearch.trim().toLowerCase();
    const advancedCount = [
        useAdvanced.location !== "ALL",
        useAdvanced.favorite !== "ALL",
        useAdvanced.content !== "ALL",
    ].filter(Boolean).length;
    const isFilteringPages = Boolean(query || advancedCount);
    const matchedPages = useMemo(() => usePages.filter((page) => {
        const haystack = `${page.title || ""} ${page.content || ""}`.toLowerCase();
        if (query && !haystack.includes(query)) return false;
        if (useAdvanced.location === "ROOT" && page.parentId) return false;
        if (useAdvanced.location === "CHILD" && !page.parentId) return false;
        if (useAdvanced.favorite === "YES" && !page.isFavorite) return false;
        if (useAdvanced.favorite === "NO" && page.isFavorite) return false;
        const hasContent = Boolean(String(page.content || "").trim());
        if (useAdvanced.content === "WITH" && !hasContent) return false;
        if (useAdvanced.content === "EMPTY" && hasContent) return false;
        return true;
    }), [query, usePages, useAdvanced]);
    const visibleIds = useMemo(() => {
        if (!isFilteringPages) return null;
        const byId = new Map(usePages.map((page) => [page.id, page]));
        const visible = new Set();

        matchedPages.forEach((page) => {
            visible.add(page.id);
            let parentId = page.parentId;
            const visited = new Set();
            while (parentId && !visited.has(parentId)) {
                visited.add(parentId);
                visible.add(parentId);
                parentId = byId.get(parentId)?.parentId || null;
            }
        });
        return visible;
    }, [isFilteringPages, matchedPages, usePages]);

    const clearAdvanced = () => setAdvanced({ location: "ALL", favorite: "ALL", content: "ALL" });

    const onToggleCollapsed = (pageId) => {
        setCollapsed((current) => {
            const next = new Set(current);
            if (next.has(pageId)) next.delete(pageId);
            else next.add(pageId);
            return next;
        });
    };

    const renderTree = (nodes, depth = 0, openInFullscreen = false) => nodes.map((node) => {
        if (visibleIds && !visibleIds.has(node.id)) return null;
        const children = (node.children || []).filter((child) => !visibleIds || visibleIds.has(child.id));
        const hasChildren = children.length > 0;
        const isCollapsed = !isFilteringPages && useCollapsed.has(node.id);

        return (
            <div key={node.id} className="LStudio__manual_tree_node">
                <div
                    className="LStudio__manual_tree_item"
                    data-selected={node.id === useSelectedId}
                    style={{ "--manual-depth": depth }}
                    onClick={() => onSelectPage(node.id, { openFullscreen: openInFullscreen })}
                >
                    <button type="button" className="LStudio__manual_tree_chevron"
                        aria-label={isCollapsed ? "Expandir" : "Contraer"}
                        disabled={!hasChildren}
                        onClick={(event) => { event.stopPropagation(); if (hasChildren) onToggleCollapsed(node.id); }}>
                        {hasChildren ? (isCollapsed ? <FaChevronRight /> : <FaChevronDown />) : <span />}
                    </button>
                    <span className="LStudio__manual_tree_icon">{node.icon || <FaFileAlt />}</span>
                    <span className="LStudio__manual_tree_title">{node.title}</span>
                    {node.isFavorite && <FaStar className="LStudio__manual_tree_star" />}
                    {!IS_READ_ONLY && (
                        <button type="button" className="LStudio__manual_tree_add" title="Nueva subpagina"
                            onClick={(event) => { event.stopPropagation(); onCreatePage(node.id); }}><FaPlus /></button>
                    )}
                </div>
                {hasChildren && !isCollapsed && (
                    <div className="LStudio__manual_tree_children">{renderTree(children, depth + 1, openInFullscreen)}</div>
                )}
            </div>
        );
    });

    const outline = useMemo(() => getMarkdownOutline(useDraft.content || ""), [useDraft.content]);
    const pagePath = useMemo(() => selectedPage ? getPagePath(usePages, selectedPage.id) : [], [usePages, selectedPage]);
    const descendants = useMemo(() => selectedPage ? collectDescendants(usePages, selectedPage.id) : new Set(), [usePages, selectedPage]);
    const parentOptions = useMemo(() => usePages.filter((page) =>
        page.id !== selectedPage?.id && !descendants.has(page.id)), [usePages, selectedPage, descendants]);
    const remainingLegacy = Math.max(0, Number(CURRENT_MANUAL?.legacyEntryCount || 0)
        - usePages.filter((page) => page.legacyEntryId).length);

    const saveLabel = {
        dirty: "Cambios sin guardar",
        saving: "Guardando...",
        saved: "Guardado",
        error: "Error al guardar",
    }[useSaveState] || "Guardado";

    const renderPageMenu = (context = "inline") => {
        if (!selectedPage) return null;
        const isOpen = usePageMenuOpen === context;
        return (
            <div className="LStudio__manual_page_menu_wrap">
                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                    icon={<FaEllipsisH />} aria-label="Mas opciones"
                    onClick={() => setPageMenuOpen((current) => current === context ? null : context)} />
                {isOpen && (
                    <div className="LStudio__manual_page_menu" role="menu">
                        <button type="button" role="menuitem" onClick={() => { setPageMenuOpen(null); onChangeIcon(); }}>
                            <span className="LStudio__manual_menu_emoji">{useDraft.icon || "📄"}</span><span><strong>Cambiar icono</strong><small>Emoji o simbolo de la pagina</small></span>
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setPageMenuOpen(null); onToggleFavorite(); }}>
                            {selectedPage.isFavorite ? <FaStar /> : <FaRegStar />}<span><strong>{selectedPage.isFavorite ? "Quitar de favoritos" : "Marcar como favorita"}</strong><small>Acceso rapido desde el manual</small></span>
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setPageMenuOpen(null); onDuplicatePage(); }}>
                            <FaCopy /><span><strong>Duplicar pagina</strong><small>Crear una copia independiente</small></span>
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setPageMenuOpen(null); onExportPageMarkdown(); }}>
                            <FaDownload /><span><strong>Exportar Markdown</strong><small>Descargar esta pagina como .md</small></span>
                        </button>
                        <button type="button" role="menuitem" onClick={() => { setPageMenuOpen(null); onExportPagePdf(); }}>
                            <FaFileAlt /><span><strong>Exportar PDF</strong><small>Descargar esta pagina como PDF</small></span>
                        </button>
                        <div className="LStudio__manual_page_menu_divider" />
                        <button type="button" role="menuitem" className="is-danger" onClick={() => { setPageMenuOpen(null); onArchivePage(); }}>
                            <FaArchive /><span><strong>Archivar página</strong><small>La rama completa va a Archivados</small></span>
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            {useFullscreenOpen && (
                <div className="LStudio__manual_fullscreen_backdrop" role="presentation" data-workspace-mode={useWorkspaceMode}>
                    <section
                        className="LStudio__manual_fullscreen"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`${IS_READ_ONLY ? "Lectura" : "Edicion"}: ${CURRENT_MANUAL?.name || "Manual"}`}
                    >
                        <header className="LStudio__manual_fullscreen_header">
                            <div className="LStudio__manual_fullscreen_identity" style={{ "--manual-color": CURRENT_MANUAL?.color || "#e83a52" }}>
                                <span><FaBookOpen /></span>
                                <div>
                                    <strong>{CURRENT_MANUAL?.name || "Manual"}</strong>
                                    <small>{IS_READ_ONLY ? "Modo lectura" : "Modo edicion"}</small>
                                </div>
                            </div>

                            <div className="LStudio__manual_fullscreen_breadcrumbs">
                                {selectedPage ? pagePath.map((page, index) => (
                                    <span key={page.id}>
                                        {index > 0 && <FaChevronRight />}
                                        <button type="button" onClick={() => onSelectPage(page.id)}>{page.title}</button>
                                    </span>
                                )) : <span className="LStudio__manual_fullscreen_breadcrumb_empty">{IS_LOADING ? "Cargando manual..." : "Sin pagina seleccionada"}</span>}
                            </div>

                            <div className="LStudio__manual_fullscreen_actions">
                                <CardActionMenu
                                    title="Opciones"
                                    ariaLabel="Opciones del manual"
                                    items={[
                                        ...(!IS_READ_ONLY ? [
                                            { key: "save", label: "Guardar ahora", icon: <FaSave />, isDisabled: !selectedPage || !hasDraftChanges, onClick: () => onSaveDraft() },
                                            { key: "config", label: "Configurar manual", icon: <FaInfoCircle />, onClick: () => setManualOpen(true) },
                                            { divider: true },
                                        ] : []),
                                        { key: "pdf-all", label: "Descargar PDF completo", icon: <FaFileAlt />, isDisabled: usePages.length === 0, onClick: onExportAllPdf },
                                        { key: "md-folders", label: "Descargar Markdown por carpetas (.zip)", icon: <FaDownload />, isDisabled: usePages.length === 0, onClick: onExportAllMarkdown },
                                    ]}
                                />
                                {IS_READ_ONLY ? (
                                    <span className="LStudio__manual_readonly_badge"><FaEye /> Solo lectura</span>
                                ) : (
                                    <>
                                        <div className="LStudio__manual_save_state" data-state={useSaveState}>
                                            {useSaveState === "saving" ? <span className="LStudio__manual_save_dot" /> : <FaSave />}
                                            {saveLabel}
                                        </div>
                                        {selectedPage && (
                                            <>
                                                <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaPlus />}
                                                    onClick={() => onCreatePage(selectedPage.id)}>Subpagina</AppButton>
                                                <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                                    icon={selectedPage.isFavorite ? <FaStar /> : <FaRegStar />} aria-label="Favorito" onClick={onToggleFavorite} />
                                                {renderPageMenu("fullscreen")}
                                            </>
                                        )}
                                    </>
                                )}
                                <button type="button" className="LStudio__manual_fullscreen_close" aria-label="Cerrar manual" onClick={onCloseFullscreen}>
                                    <FaTimes />
                                </button>
                            </div>
                        </header>

                        <div className="LStudio__manual_fullscreen_body">
                            <aside className="LStudio__manual_fullscreen_nav">
                                <div className="LStudio__manual_book_nav_head">
                                    <div className="LStudio__manual_page_filterbar">
                                        <div className="LStudio__manual_page_search">
                                            <FaSearch />
                                            <input
                                                id="manual-page-search-fullscreen"
                                                value={useSearch}
                                                onChange={(event) => setSearch(event.target.value)}
                                                placeholder="Buscar paginas... Ctrl+K"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            className="LStudio__manual_page_filter_toggle"
                                            data-open={useAdvancedOpen ? "true" : "false"}
                                            onClick={() => setAdvancedOpen((current) => !current)}
                                            title={advancedCount ? `Filtros avanzados (${advancedCount})` : "Filtros avanzados"}
                                            aria-label="Filtros avanzados"
                                        >
                                            <FaSlidersH />
                                        </button>
                                    </div>

                                    <AdvancedFilterPanel
                                        isOpen={useAdvancedOpen}
                                        activeCount={advancedCount}
                                        className="LStudio__manual_page_advanced"
                                        onClear={clearAdvanced}
                                    >
                                        <FieldSelect
                                            id="manual_page_filter_location"
                                            label="Ubicacion"
                                            value={useAdvanced.location}
                                            onChange={(event) => setAdvanced((current) => ({ ...current, location: event.target.value }))}
                                            options={[
                                                { value: "ALL", label: "Todas las paginas" },
                                                { value: "ROOT", label: "Nivel principal" },
                                                { value: "CHILD", label: "Subpaginas" },
                                            ]}
                                        />
                                        <FieldSelect
                                            id="manual_page_filter_favorite"
                                            label="Destacado"
                                            value={useAdvanced.favorite}
                                            onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))}
                                            options={[
                                                { value: "ALL", label: "Todos" },
                                                { value: "YES", label: "Destacadas" },
                                                { value: "NO", label: "No destacadas" },
                                            ]}
                                        />
                                        <FieldSelect
                                            id="manual_page_filter_content"
                                            label="Contenido"
                                            value={useAdvanced.content}
                                            onChange={(event) => setAdvanced((current) => ({ ...current, content: event.target.value }))}
                                            options={[
                                                { value: "ALL", label: "Todas" },
                                                { value: "WITH", label: "Con contenido" },
                                                { value: "EMPTY", label: "Sin contenido" },
                                            ]}
                                        />
                                    </AdvancedFilterPanel>

                                    <div className="LStudio__manual_page_filter_count">
                                        {matchedPages.length} {matchedPages.length === 1 ? "pagina" : "paginas"}
                                    </div>

                                    {!IS_READ_ONLY && (
                                        <button type="button" className="LStudio__manual_new_page" onClick={() => onCreatePage(null)}>
                                            <FaPlus /> Nueva pagina
                                        </button>
                                    )}
                                </div>

                                <div className="LStudio__manual_tree">
                                    {IS_LOADING && <div className="LStudio__manual_tree_empty">Cargando paginas...</div>}
                                    {!IS_LOADING && usePages.length === 0 && (
                                        <div className="LStudio__manual_tree_empty">
                                            <FaFolderOpen />
                                            <span>Este manual todavia no tiene paginas.</span>
                                            {!IS_READ_ONLY && <button type="button" onClick={() => onCreatePage(null)}>Crear la primera</button>}
                                        </div>
                                    )}
                                    {!IS_LOADING && usePages.length > 0 && isFilteringPages && matchedPages.length === 0 && (
                                        <div className="LStudio__manual_tree_empty">No hay paginas que coincidan con la busqueda o los filtros.</div>
                                    )}
                                    {renderTree(tree, 0, true)}
                                </div>

                                {!IS_READ_ONLY && remainingLegacy > 0 && (
                                    <div className="LStudio__manual_legacy">
                                        <FaHistory />
                                        <div>
                                            <strong>{remainingLegacy} entrada{remainingLegacy === 1 ? "" : "s"} de la version anterior</strong>
                                            <span>Podes convertirlas a paginas Markdown sin borrar el respaldo.</span>
                                        </div>
                                        <button type="button" disabled={useImporting} onClick={onImportLegacy}>
                                            {useImporting ? "Convirtiendo..." : "Convertir a paginas"}
                                        </button>
                                    </div>
                                )}
                            </aside>

                            <main className="LStudio__manual_fullscreen_main" data-readonly={IS_READ_ONLY ? "true" : "false"}>
                                {IS_LOADING && !selectedPage && (
                                    <div className="LStudio__manual_fullscreen_empty">
                                        <FaBookOpen />
                                        <strong>Cargando manual...</strong>
                                    </div>
                                )}

                                {!IS_LOADING && !selectedPage && (
                                    <div className="LStudio__manual_fullscreen_empty">
                                        <FaBookOpen />
                                        <h2>{IS_READ_ONLY ? "Este manual no tiene contenido todavia" : "Este manual esta listo para escribir"}</h2>
                                        <p>{IS_READ_ONLY ? "Cuando tenga paginas vas a poder recorrerlas desde este mismo modal." : "Crea una pagina y empeza a documentar libremente."}</p>
                                        {!IS_READ_ONLY && <AppButton icon={<FaPlus />} onClick={() => onCreatePage(null)}>Nueva pagina</AppButton>}
                                    </div>
                                )}

                                {selectedPage && (
                                    <>
                                        <div className={`LStudio__manual_fullscreen_title ${IS_READ_ONLY ? "LStudio__manual_fullscreen_title--readonly" : ""}`}>
                                            {IS_READ_ONLY ? (
                                                <>
                                                    <span className="LStudio__manual_fullscreen_title_icon">{useDraft.icon || "📄"}</span>
                                                    <h1>{useDraft.title || selectedPage.title}</h1>
                                                </>
                                            ) : (
                                                <>
                                                    <button type="button" className="LStudio__manual_page_icon" onClick={onChangeIcon} title="Cambiar icono">
                                                        {useDraft.icon || "📄"}
                                                    </button>
                                                    <input
                                                        ref={titleRef}
                                                        value={useDraft.title}
                                                        onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                                                        onBlur={() => { if (useDraft.title.trim()) onSaveDraft(); }}
                                                        placeholder="Titulo de la pagina"
                                                        maxLength={180}
                                                    />
                                                </>
                                            )}
                                        </div>

                                        <ManualMarkdownEditor
                                            value={useDraft.content}
                                            onChange={IS_READ_ONLY ? undefined : (content) => setDraft((current) => ({ ...current, content }))}
                                            mode={IS_READ_ONLY ? "preview" : useEditorMode}
                                            onModeChange={IS_READ_ONLY ? undefined : setEditorMode}
                                            onSave={IS_READ_ONLY ? undefined : onSaveDraft}
                                            scrollHeadingIndex={useScrollHeadingIndex}
                                            readOnly={IS_READ_ONLY}
                                            placeholder="Escribi libremente. Usa Markdown o los bloques de la barra: codigo, checklist, callout, tabla..."
                                        />
                                    </>
                                )}
                            </main>

                            <aside className="LStudio__manual_fullscreen_outline">
                                {selectedPage && (
                                    <>
                                        <section className="LStudio__manual_outline_section">
                                            <strong>En esta pagina</strong>
                                            {outline.length ? (
                                                <nav className="LStudio__manual_outline_nav">
                                                    {outline.map((item) => (
                                                        <button key={`${item.index}-${item.title}`} type="button"
                                                            style={{ "--outline-level": item.level - 2 }}
                                                            onClick={() => {
                                                                if (!IS_READ_ONLY && useEditorMode === "write") setEditorMode("split");
                                                                setScrollHeadingIndex(null);
                                                                window.setTimeout(() => {
                                                                    setScrollHeadingIndex(item.index);
                                                                    window.setTimeout(() => setScrollHeadingIndex(null), 450);
                                                                }, 0);
                                                            }}>
                                                            {item.title}
                                                        </button>
                                                    ))}
                                                </nav>
                                            ) : <span className="LStudio__manual_outline_empty">Sin titulos ## para mostrar en el indice.</span>}
                                        </section>

                                        {IS_READ_ONLY ? (
                                            <section className="LStudio__manual_readonly_meta">
                                                <ReadOnlyInfo
                                                    heading="Pagina"
                                                    createdAt={selectedPage.createdAt}
                                                    updatedAt={selectedPage.updatedAt}
                                                    createdLabel="Creada"
                                                    updatedLabel="Actualizada"
                                                />
                                            </section>
                                        ) : (
                                            <section className="LStudio__manual_page_settings">
                                                <strong>Pagina</strong>
                                                <FieldSelect id="page_parent" label="Ubicación" value={selectedPage.parentId || ""} onChange={(event) => onChangeParent(event.target.value)} options={[{ value: "", label: "Nivel principal" }, ...sortPages(parentOptions).map((page) => ({ value: page.id, label: page.title }))]} isSearchable={parentOptions.length >= 8} placeholder="Nivel principal" />
                                                <div className="LStudio__manual_setting_buttons">
                                                    <button type="button" onClick={() => onMovePage(-1)}><FaArrowUp /> Subir</button>
                                                    <button type="button" onClick={() => onMovePage(1)}><FaArrowDown /> Bajar</button>
                                                    {selectedPage.parentId && <button type="button" onClick={() => onChangeParent("")}><FaLevelUpAlt /> Llevar a raiz</button>}
                                                </div>
                                                <div className="LStudio__manual_setting_buttons LStudio__manual_setting_buttons--stack">
                                                    <button type="button" onClick={onExportPageMarkdown}><FaDownload /> Exportar pagina .md</button>
                                                    <button type="button" onClick={onExportPagePdf}><FaFileAlt /> Exportar pagina PDF</button>
                                                    <button type="button" onClick={onDuplicatePage}><FaCopy /> Duplicar pagina</button>
                                                    <button type="button" className="is-danger" onClick={onArchivePage}><FaArchive /> Archivar página</button>
                                                </div>
                                                <div className="LStudio__manual_page_meta">
                                                    <span>Creada {selectedPage.createdAt ? new Date(selectedPage.createdAt).toLocaleDateString("es-AR") : "-"}</span>
                                                    <span>Actualizada {selectedPage.updatedAt ? new Date(selectedPage.updatedAt).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" }) : "-"}</span>
                                                </div>
                                            </section>
                                        )}
                                    </>
                                )}
                            </aside>
                        </div>
                    </section>
                </div>
            )}

            {!IS_READ_ONLY && (
                <ManualFormModal
                    isOpen={useManualOpen}
                    onClose={() => setManualOpen(false)}
                    manual={CURRENT_MANUAL}
                    onSave={onSaveManual}
                    isLoading={useActionApiIsLoading}
                />
            )}
        </>
    );
};
